/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useTranslation } from "react-i18next";
import {
	AutoclaveEquipmentsDetail,
	SterilizationParameters,
} from "../emptySterilization.types";
import { useState } from "react";

interface Props {
	sterilizationParameters: SterilizationParameters;
	setSterilizationParameters: React.Dispatch<
		React.SetStateAction<SterilizationParameters>
	>;
	autoclaveEquipments: AutoclaveEquipmentsDetail[];
	errors: {
		batch: string;
		autoclave: string;
		cycleNumber: string;
		temperature: string;
		pressure: string;
		startDate: string;
		endDate: string;
		table: string;
	};

	setErrors: React.Dispatch<
		React.SetStateAction<{
			batch: string;
			autoclave: string;
			cycleNumber: string;
			temperature: string;
			pressure: string;
			startDate: string;
			endDate: string;
			table: string;
		}>
	>;
}

const SterilizationCycleParameters = ({
	sterilizationParameters,
	setSterilizationParameters,
	autoclaveEquipments,
	errors,
	setErrors
}: Props) => {
	const { t } = useTranslation();
	var storedData = localStorage.getItem("userDetails");
	const userDetails = storedData ? JSON.parse(storedData) : null;

	const [selectedEq, setSelectedEq] =
		useState<AutoclaveEquipmentsDetail | null>(null);

	const handleChange = (key: keyof SterilizationParameters, value: string) => {
		// Clear the corresponding error
		setErrors((prev) => ({
			...prev,
			...(key === "autoclave_id" && { autoclave: "" }),
			...(key === "cycle_number" && { cycleNumber: "" }),
			...(key === "temperature_celsius" && { temperature: "" }),
			...(key === "pressure_bar" && { pressure: "" }),
			...(key === "cycle_start_datetime" && { startDate: "" }),
			...(key === "cycle_end_datetime" && { endDate: "" }),
		}));

		setSterilizationParameters((prev) => {
			const updatedState = { ...prev, [key]: value };

			if (key === "cycle_start_datetime" || key === "cycle_end_datetime") {
				const startMs = new Date(updatedState.cycle_start_datetime).getTime();
				const endMs = new Date(updatedState.cycle_end_datetime).getTime();

				if (startMs && endMs && endMs > startMs) {
					updatedState.duration_min = String(
						Math.floor((endMs - startMs) / 60000)
					);
				} else {
					updatedState.duration_min = "";
				}
			}

			return updatedState;
		});

		if (key === "autoclave_id" && value) {
			const eq = autoclaveEquipments.find(
				(eq) => String(eq.id) === String(value),
			);
			setSelectedEq(eq ?? null);
		} else if (key === "autoclave_id") {
			setSelectedEq(null);
		}
	};

	return (
		<div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
			<div className="px-8 py-2 border-b border-slate-100 bg-slate-50/50 flex items-center gap-3">
				<span className="material-symbols-outlined text-orange-500 text-xl">
					settings_suggest
				</span>
				<h3 className="text-xs font-black text-[#1E293B] uppercase tracking-[0.15em] mt-2">
					{t(
						"postAutoclaveProcess.emptySterilization.cycleParameters.title",
					)}
				</h3>
			</div>
			<div className="p-6 grid grid-cols-3 gap-8">
				{/*    Top Row    */}
				<div className="space-y-2">
					<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
						{t(
							"postAutoclaveProcess.emptySterilization.cycleParameters.autoclaveId",
						)}  <span className="text-red-500 ml-1">*</span>
					</label>
					<select
						value={sterilizationParameters.autoclave_id ?? ""}
						onChange={(e) => handleChange("autoclave_id", e.target.value)}
						className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[12px] text-slate-700 outline-none focus:border-[#FF7043] transition-all"
					>
						<option value="" disabled>
							{t(
								"postAutoclaveProcess.emptySterilization.cycleParameters.selectAutoclave",
							)}
						</option>
						{autoclaveEquipments.map((eq) => (
							<option key={eq.id} value={eq.id}>
								{eq.name}
							</option>
						))}
					</select>
					{errors.autoclave && (
						<p className="mt-1 text-[11px] text-red-500">
							{errors.autoclave}
						</p>
					)}
				</div>
				<div className="space-y-2">
					<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
						{t(
							"postAutoclaveProcess.emptySterilization.cycleParameters.cycleNumber",
						)}  <span className="text-red-500 ml-1">*</span>
					</label>
					<input
						type="text"
						value={sterilizationParameters.cycle_number}
						onChange={(e) => handleChange("cycle_number", e.target.value)}
						className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[12px] text-slate-700 outline-none focus:border-[#FF7043] transition-all"
					/>
					{errors.cycleNumber && (
						<p className="mt-1 text-[11px] text-red-500">
							{errors.cycleNumber}
						</p>
					)}
				</div>

				<div className="space-y-2">
					<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
						Autoclave Type
					</label>
					<input
						type="text"
						readOnly
						value={selectedEq?.equipment_type_name || ""}
						className="w-full px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg text-xs font-bold text-slate-500 outline-none"
					/>
				</div>

				{/*    2 Row    */}
				<div className="space-y-2">
					<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
						{t(
							"postAutoclaveProcess.emptySterilization.cycleParameters.temperature",
						)}  <span className="text-red-500 ml-1">*</span>
					</label>
					<div className="relative">
						<input
							type="number"
							value={sterilizationParameters.temperature_celsius}
							onChange={(e) =>
								handleChange("temperature_celsius", e.target.value)
							}
							className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[12px] text-slate-700 outline-none focus:border-[#FF7043] transition-all"
						/>
						<span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-black text-slate-300 uppercase tracking-widest">
							°C
						</span>
						{errors.temperature && (
							<p className="mt-1 text-[11px] text-red-500">
								{errors.temperature}
							</p>
						)}
					</div>
				</div>
				<div className="space-y-2">
					<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
						{t(
							"postAutoclaveProcess.emptySterilization.cycleParameters.pressure",
						)}  <span className="text-red-500 ml-1">*</span>
					</label>
					<div className="relative">
						<input
							type="number"
							value={sterilizationParameters.pressure_bar}
							onChange={(e) =>
								handleChange("pressure_bar", e.target.value)
							}
							className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[12px] text-slate-700 outline-none focus:border-[#FF7043] transition-all"
						/>
						<span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-black text-slate-300 uppercase tracking-widest">
							Bar
						</span>
						{errors.pressure && (
							<p className="mt-1 text-[11px] text-red-500">
								{errors.pressure}
							</p>
						)}
					</div>
				</div>

				<div className="space-y-2">
					<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
						{t("postAutoclaveProcess.emptySterilization.operatorName")}
					</label>
					<div className="flex items-center gap-3 px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg">
						<div className="w-4.5 h-4.5 bg-white rounded-full flex items-center justify-center border border-slate-200">
							<span className="material-symbols-outlined text-xs text-slate-400">
								person
							</span>
						</div>
						<span className="text-xs font-bold text-slate-600">
							{userDetails?.[0]?.first_name +
								" " +
								userDetails?.[0]?.last_name}
						</span>
					</div>
				</div>

				{/*    3 Row    */}
				<div className="space-y-2">
					<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
						{t(
							"postAutoclaveProcess.emptySterilization.cycleParameters.cycleStartTime",
						)}  <span className="text-red-500 ml-1">*</span>
					</label>
					<input
						type="datetime-local"
						value={sterilizationParameters.cycle_start_datetime}
						onChange={(e) =>
							handleChange("cycle_start_datetime", e.target.value)
						}
						className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[12px] text-slate-700 outline-none focus:ring-2 focus:ring-orange-100 focus:border-orange-500 transition-all [direction:rtl] [&::-webkit-calendar-picker-indicator]:ml-0"
					/>
					{errors.startDate && (
						<p className="mt-1 text-[11px] text-red-500">
							{errors.startDate}
						</p>
					)}
				</div>
				<div className="space-y-2">
					<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
						{t(
							"postAutoclaveProcess.emptySterilization.cycleParameters.cycleEndTime",
						)}  <span className="text-red-500 ml-1">*</span>
					</label>
					<input
						type="datetime-local"
						value={sterilizationParameters.cycle_end_datetime}
						onChange={(e) =>
							handleChange("cycle_end_datetime", e.target.value)
						}
						className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[12px] text-slate-700 outline-none focus:ring-2 focus:ring-orange-100 focus:border-orange-500 transition-all [direction:rtl] [&::-webkit-calendar-picker-indicator]:ml-0"
					/>
					{errors.endDate && (
						<p className="mt-1 text-[11px] text-red-500">
							{errors.endDate}
						</p>
					)}
				</div>
				<div className="space-y-2">
					<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
						{t(
							"postAutoclaveProcess.emptySterilization.cycleParameters.duration",
						)}
					</label>
					<div className="relative">
						<input
							type="number"
							readOnly
							value={sterilizationParameters.duration_min}
							className="w-full px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg text-[12px] font-bold text-slate-500 outline-none cursor-not-allowed"
						/>
						<span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
							Min
						</span>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SterilizationCycleParameters;
